"use client";
import React, { useState } from "react";
import Image from "next/image";
import placeholder from "../../images/placeholder.png";

// Best-effort readable label from a filename, e.g. "mumbai-skyline_2.png" -> "Mumbai skyline 2"
function humanizeFilename(filename) {
  if (!filename || typeof filename !== "string") return null;

  const withoutExt = filename.replace(/\.[a-z0-9]+$/i, "");
  const spaced = withoutExt.replace(/[-_]+/g, " ").trim();
  if (!spaced) return null;

  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// CMS image objects carry their own alt text (alt_name, image_alt_name, alongside url) —
// prefer that, then any legacy/alt field, then the caller's own fallback text,
// then a best-effort guess from the filename.
export function resolveAlt(source, fallbackAlt) {
  const entry = Array.isArray(source) ? source[0] : source;
  const cmsAlt =
    entry && typeof entry === "object"
      ? entry.alt_name || entry.image_alt_name || entry.title
      : null;

  return cmsAlt || fallbackAlt || humanizeFilename(entry?.name) || "Image";
}

const isTransparentFormat = (url = "") => {
  return /\.(png|webp|svg)($|\?)/i.test(url);
};

const isJpgFormat = (url = "") => {
  return /\.(jpg|jpeg)($|\?)/i.test(url);
};

/**
 * Extracts image URL with priority: md -> sd -> thumbnail -> url
 * Accepts array of image objects, single image object, or string URL.
 * Automatically preserves transparency: if original is a transparent PNG/SVG
 * and the conversion is a JPG (which lacks alpha channel and turns black),
 * it skips the JPG conversion and falls back to the transparent original.
 */
export function getImageUrl(source, options = {}) {
  const {
    useUrl = false,
    preferOriginal = false,
    preserveTransparency = true,
  } = options;
  if (!source) return "";

  if (Array.isArray(source)) {
    if (source.length === 0) return "";
    return getImageUrl(source[0], options);
  }

  if (typeof source === "object" && source !== null) {
    const originalUrl = source.url || source.src || "";
    const originalName = source.name || "";
    const mimeType = source.mime_type || "";

    // When useUrl (or preferOriginal) is enabled, directly use the original url
    if ((useUrl || preferOriginal) && originalUrl) {
      return originalUrl;
    }

    const isOriginalTransparent =
      mimeType === "image/png" ||
      mimeType === "image/svg+xml" ||
      isTransparentFormat(originalUrl) ||
      isTransparentFormat(originalName);

    const candidates = [
      source.md,
      source.sd,
      source.thumbnail,
      source.url,
      source.src,
    ].filter((c) => typeof c === "string" && c.trim() !== "");

    for (const candidate of candidates) {
      // If original is transparent (PNG/SVG) and conversion is JPG,
      // skip JPG conversion to prevent black background
      if (
        preserveTransparency &&
        isOriginalTransparent &&
        isJpgFormat(candidate) &&
        originalUrl &&
        !isJpgFormat(originalUrl)
      ) {
        continue;
      }
      return candidate;
    }

    return originalUrl || "";
  }

  if (typeof source === "string") return source.trim();

  return "";
}

export function resolveSrc(source, options = {}) {
  const url = getImageUrl(source, options);
  const fallback = options.fallbackSrc
    ? typeof options.fallbackSrc === "string"
      ? options.fallbackSrc
      : options.fallbackSrc.src
    : placeholder.src;
  return url || fallback;
}

const AppImage = ({
  src,
  image,
  alt,
  className,
  width,
  height,
  unoptimized,
  useUrl = false,
  preferOriginal,
  preserveTransparency = true,
  fallbackSrc,
  ...props
}) => {
  const source = image !== undefined ? image : src;
  const defaultFallback = fallbackSrc
    ? typeof fallbackSrc === "string"
      ? fallbackSrc
      : fallbackSrc.src
    : placeholder.src;

  const resolvedSrc = resolveSrc(source, {
    useUrl,
    preferOriginal,
    preserveTransparency,
    fallbackSrc: defaultFallback,
  });
  const [erroredSrc, setErroredSrc] = useState(null);
  const imgSrc = erroredSrc === resolvedSrc ? defaultFallback : resolvedSrc;

  const isSvgOrData =
    typeof imgSrc === "string" &&
    (imgSrc.endsWith(".svg") || imgSrc.startsWith("data:"));
  const shouldUnoptimize =
    unoptimized !== undefined ? unoptimized : isSvgOrData;

  return (
    <Image
      src={imgSrc}
      alt={resolveAlt(source, alt)}
      className={className}
      onError={(e) => {
        if (resolvedSrc !== defaultFallback) {
          setErroredSrc(resolvedSrc);
        }
        if (props.onError) props.onError(e);
      }}
      unoptimized={shouldUnoptimize}
      sizes={
        props.sizes ||
        (props.fill
          ? "100vw"
          : "(max-width: 768px) 90vw, (max-width: 1200px) 45vw, 400px")
      }
      {...props}
      width={width}
      height={height}
    />
  );
};

export default AppImage;

